// Screen outline for NoVA — a compact, redacted plain-text description of
// what the user is looking at RIGHT NOW (which tab is open, the headings,
// the buttons, the empty state, the red banner), captured from the rendered
// DOM when a turn is sent. The console catalog is the map; this is the
// territory. Same file in the control plane and the App Kit foundation —
// edit both.
//
// Conventions a page can use (also honoured by generated kit screens):
//   data-nova-note="Step 2 of 5: waiting for the research run"  → quoted verbatim (≤320 chars)
//   data-nova-describe="Workflow: 7 blocks…"                      → one long-form description
//                                                                   per screen (≤1000 chars): a
//                                                                   canvas, a board, a record
//   data-nova-ignore                                              → subtree skipped
//   (data-agent-note / data-agent-ignore are aliases, same meaning)
//   data-agent-noclick / data-agent-guard                         → printed "(operator-only)"
//
// Outline handles (2026-10-17, agent page controls): every button / link /
// tab / field the outline prints gets a short handle — `Save[b2]`, `Lab[t3]`,
// `Search[f1]` — and the element behind it is kept here (weakly) so
// pageControls.resolveTarget('b2') hits the exact control the model read,
// not a lookalike. Rebuilt on every capture.
//
// Privacy: password / email / tel / payment inputs and anything that looks
// like a secret, card number or email are emitted as [redacted]; other
// values are clipped to 40 chars. Nothing here reaches a model until the
// caller sends it; the backend applies its own cap (agentos.screen).

import { getBuildFreshness } from './buildFreshness.js';

const IGNORE = '[data-nova-ignore], [data-agent-ignore], .cp-panel, .cp-fab, .va-panel, .va-launcher, .va-rail, .anr, .nv-stage-strip, .nv-stage-ticker, script, style, svg, canvas, noscript, template';
const AGENT_UI = '.cp-panel, .cp-fab, .va-panel, .va-launcher, .va-rail, .anr, .nv-stage-strip, .nv-stage-ticker';
const CONTAINER = '[role="alert"], [role="status"], .cs-banner, .cs-empty, [role="tablist"], .cs-tabs, table';
const SECRET_RE = /pass|secret|token|api[-_ ]?key|card|ssn|cvv|cvc|otp|pin\b/i;
const CARD_RE = /^[\d\s-]{13,23}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

let _handles = new Map();
const WR = globalThis.WeakRef; // eslint-disable-line no-undef
const _ref = (el) => (typeof WR === 'function' ? new WR(el) : { deref: () => el });
export function getOutlineElement(handle) {
  const r = _handles.get(String(handle || '').toLowerCase());
  const el = r?.deref?.();
  return el && el.isConnected ? el : null;
}

const text = (el, n = 160) => String(el?.textContent || '').replace(/\s+/g, ' ').trim().slice(0, n);
const clip = (s, n) => (s.length > n ? `${s.slice(0, n)}…` : s);

function hidden(el, win) {
  if (el.hidden || el.getAttribute('aria-hidden') === 'true') return true;
  if (el.tagName === 'DIALOG' && !el.open) return true;
  try {
    const cs = win.getComputedStyle(el);
    return cs.display === 'none' || cs.visibility === 'hidden';
  } catch { return false; }
}

// Viewport position marker. In-view elements carry no marker so the model
// can tell "on screen" from "above (already scrolled past)" and "below".
function posMark(el, win) {
  try {
    const r = el.getBoundingClientRect();
    if (r.height <= 0 && r.width <= 0) return '';
    const h = win.innerHeight || 0;
    if (r.bottom < 0) return ' (above)';
    if (r.top > h) return ' (below)';
    return '';
  } catch { return ''; }
}

function fieldLabel(el, doc) {
  const aria = el.getAttribute('aria-label');
  if (aria) return aria.trim();
  const by = el.getAttribute('aria-labelledby');
  if (by) { const t = text(doc.getElementById(by), 80); if (t) return t; }
  if (el.id) {
    const l = Array.from(doc.querySelectorAll('label[for]')).find((x) => x.getAttribute('for') === el.id);
    if (l) return text(l, 80);
  }
  const wrap = el.closest('label');
  if (wrap) {
    const t = text(wrap, 80).replace(text(el, 80), '').trim();
    if (t) return t;
  }
  return el.getAttribute('placeholder') || el.getAttribute('name') || '';
}

function fieldValue(el) {
  const tag = el.tagName;
  const type = (el.getAttribute('type') || (tag === 'TEXTAREA' ? 'textarea' : tag === 'SELECT' ? 'select' : 'text')).toLowerCase();
  if (type === 'checkbox' || type === 'radio') return { type, value: el.checked ? 'checked' : 'unchecked' };
  if (type === 'range') return { type, value: String(el.value ?? '') };
  if (tag === 'SELECT') {
    const opt = el.options?.[el.selectedIndex];
    return { type, value: opt ? text(opt, 40) : '' };
  }
  const raw = String(el.value || '');
  const hint = `${el.getAttribute('name') || ''} ${el.id || ''} ${el.getAttribute('autocomplete') || ''} ${el.getAttribute('aria-label') || ''}`;
  const secret = ['password', 'email', 'tel'].includes(type)
    || /^cc-/.test(el.getAttribute('autocomplete') || '')
    || SECRET_RE.test(hint)
    || (raw && (CARD_RE.test(raw) || EMAIL_RE.test(raw.trim())));
  if (!raw) return { type, value: '(empty)' };
  return { type, value: secret ? '[redacted]' : clip(raw.replace(/\s+/g, ' '), 40) };
}

function tabsLine(el, handles) {
  const tabs = Array.from(el.querySelectorAll('[role="tab"], button, a')).filter((t) => text(t));
  if (!tabs.length) return '';
  const parts = tabs.slice(0, 16).map((t) => {
    const on = t.getAttribute('aria-selected') === 'true' || t.getAttribute('aria-current') || t.classList.contains('active') || t.classList.contains('is-active') || t.classList.contains('on');
    const label = text(t, 40);
    return `${on ? `[${label}]` : label}${handles.mint('t', t)}`;
  });
  return `Tabs: ${parts.join(' · ')}`;
}

// Is this control one the agent may not press? Printed so the model does
// not try (pageControls.canActuate refuses it anyway).
const operatorOnly = (el) => !!el.closest('[data-agent-noclick], [data-agent-guard]');

// Scrollable areas in view (wide tables, side panels) — the model needs to
// know they exist to scroll them with scroll_page target=…
function scrollableNote(el, win) {
  try {
    const sh = el.scrollHeight - el.clientHeight;
    const sw = el.scrollWidth - el.clientWidth;
    if (sh <= 8 && sw <= 8) return '';
    const cs = win.getComputedStyle(el);
    const y = sh > 8 && /(auto|scroll|overlay)/.test(cs.overflowY);
    const x = sw > 8 && /(auto|scroll|overlay)/.test(cs.overflowX);
    if (!x && !y) return '';
    const parts = [];
    if (y) parts.push(`${Math.round((el.scrollTop / sh) * 100)}% down`);
    if (x) parts.push(`${Math.round((el.scrollLeft / sw) * 100)}% across`);
    return parts.join(', ');
  } catch { return ''; }
}

function tableLine(el) {
  const heads = Array.from(el.querySelectorAll('th')).map((h) => text(h, 30)).filter(Boolean).slice(0, 12);
  const rows = el.querySelectorAll('tbody tr').length || Math.max(0, el.querySelectorAll('tr').length - (heads.length ? 1 : 0));
  const cap = el.querySelector('caption');
  return `Table${cap ? ` "${text(cap, 60)}"` : ''}: ${heads.join(' | ') || '(no headers)'} (${rows} row${rows === 1 ? '' : 's'})`;
}

function dialogTitle(el, doc) {
  const aria = el.getAttribute('aria-label');
  if (aria) return aria.trim();
  const by = el.getAttribute('aria-labelledby');
  if (by) { const t = text(doc.getElementById(by), 80); if (t) return t; }
  return text(el.querySelector('h1,h2,h3,h4,[role="heading"]'), 80) || text(el, 80);
}

// The section (or header) with the largest share of the viewport.
function sectionInView(doc, win) {
  let bestId = null;
  let bestRatio = 0;
  const h = win.innerHeight || 0;
  for (const el of Array.from(doc.querySelectorAll('section[id], header[id]'))) {
    try {
      const r = el.getBoundingClientRect();
      const visible = Math.max(0, Math.min(r.bottom, h) - Math.max(r.top, 0));
      const ratio = visible / Math.max(1, h);
      if (ratio > bestRatio) { bestRatio = ratio; bestId = el.id; }
    } catch { /* detached */ }
  }
  return bestId ? { id: bestId, ratio: Math.round(bestRatio * 100) } : null;
}

// Where the visitor is on the page + screen size + agent-set zoom, so the
// model can judge what is readable (a 1500-px figure in a 400-px cell is a
// thumbnail — zoom_into it). '' when the window has no layout (tests).
function viewportLine(doc, win) {
  try {
    const vw = win.innerWidth || 0;
    const vh = win.innerHeight || 0;
    if (!vw || !vh) return '';
    const sy = Math.round(win.scrollY || 0);
    const total = Math.max(1, (doc.documentElement?.scrollHeight || 1) - vh);
    const pct = Math.min(100, Math.max(0, Math.round((sy / total) * 100)));
    const sec = sectionInView(doc, win);
    const dpr = Number(win.devicePixelRatio || 1);
    const size = `${vw}×${vh} css px${dpr && Math.abs(dpr - 1) > 0.05 ? ` @${dpr.toFixed(2).replace(/\.?0+$/, '')}x` : ''}`;
    const kind = vw >= 1200 ? 'desktop' : vw >= 760 ? 'tablet-width' : 'phone-width';
    const z = parseFloat(doc.documentElement?.style?.getPropertyValue('--nv-zoom') || '1');
    const zoomNote = Number.isFinite(z) && Math.abs(z - 1) > 0.001 ? `; page zoom ${Math.round(z * 100)}% (set_zoom)` : '';
    return `Viewport: ${size} (${kind}); ${pct}% down the page${sec ? `; section in view: ${sec.id} (${sec.ratio}% of the screen)` : ''}${zoomNote}`;
  } catch { return ''; }
}

// captureScreenOutline({ root?, maxChars = 3500, win = window }) → string
// ('' when there is no document). Never throws — a capture failure is a
// turn without screen context, not a failed turn.
export function captureScreenOutline({ root = null, maxChars = 3500, win = typeof window !== 'undefined' ? window : null } = {}) {
  try {
    const doc = win?.document;
    if (!doc?.body) return '';
    const scope = root || doc.querySelector('main') || doc.body;
    const lines = [];
    const buttons = [];
    const links = [];
    const badges = [];
    const notes = [];
    const describes = [];
    const scrollables = [];
    const seen = new Set();
    const push = (arr, s) => { if (s && !seen.has(`${arr}:${s}`)) { seen.add(`${arr}:${s}`); arr.push(s); } };
    // Fresh handle map for this capture.
    const fresh = new Map();
    const counters = { b: 0, l: 0, t: 0, f: 0 };
    const handles = {
      mint(prefix, el) {
        counters[prefix] += 1;
        const h = `${prefix}${counters[prefix]}`;
        fresh.set(h, _ref(el));
        return `[${h}]`;
      },
    };
    _handles = fresh;

    const vp = viewportLine(doc, win);
    if (vp) lines.push(vp);

    // Open dialogs first — they are what the user is looking at. A lens also
    // reports its own scroll position so "scroll down" is grounded.
    for (const d of Array.from(doc.querySelectorAll('[role="dialog"], dialog'))) {
      if (d.closest(IGNORE) || hidden(d, win) || d.closest(AGENT_UI) || d.matches(AGENT_UI)) continue;
      const body = d.querySelector('.nv-lens-scroll') || d;
      const sn = scrollableNote(body, win);
      lines.push(`Dialog open: ${dialogTitle(d, doc)}${sn ? ` — scrolled ${sn} (scroll_page scrolls it; Escape closes it)` : ''}`);
    }

    const walker = doc.createTreeWalker(scope, win.NodeFilter?.SHOW_ELEMENT ?? 1);
    let el = scope;
    let skipUntil = null;
    while (el) {
      if (skipUntil && skipUntil.contains(el)) { el = walker.nextNode(); continue; }
      skipUntil = null;
      if (el !== scope && (el.matches(IGNORE) || hidden(el, win))) { skipUntil = el; el = walker.nextNode(); continue; }

      const tag = el.tagName;
      const role = el.getAttribute('role') || '';
      const pos = posMark(el, win);
      const note = el.getAttribute('data-nova-note') || el.getAttribute('data-agent-note');
      if (note) push(notes, note.trim().slice(0, 320));
      // Long-form description of a canvas / board the outline cannot walk
      // (React Flow nodes are divs in an svg-heavy container): one per
      // screen, printed after the notes as "Describe:".
      const desc = el.getAttribute('data-nova-describe');
      if (desc && describes.length < 2) push(describes, desc.trim().slice(0, 1000));
      // Scrollable area in view (not the page itself) — name it for scroll_page.
      if (!pos && scrollables.length < 4 && el !== scope && (tag === 'DIV' || tag === 'SECTION') && !el.closest('[role="dialog"], dialog')) {
        const sn = scrollableNote(el, win);
        if (sn) {
          const name = el.getAttribute('aria-label') || text(el.querySelector('caption, h1, h2, h3, h4, th'), 50) || (el.querySelector('table') ? 'table' : 'panel');
          push(scrollables, `${name} (${sn})`);
        }
      }

      if (/^H[1-6]$/.test(tag) || role === 'heading') {
        const level = role === 'heading' ? Number(el.getAttribute('aria-level') || 2) : Number(tag[1]);
        const t = text(el, 120);
        if (t) lines.push(`${'#'.repeat(Math.min(level, 4))} ${t}${pos}`);
      } else if (el.matches(CONTAINER)) {
        if (role === 'tablist' || el.classList.contains('cs-tabs')) lines.push(tabsLine(el, handles));
        else if (tag === 'TABLE') lines.push(`${tableLine(el)}${pos}`);
        else {
          const kind = role === 'alert' ? 'Alert' : role === 'status' ? 'Status' : el.classList.contains('cs-empty') ? 'Empty state' : 'Banner';
          const t = text(el, 220);
          if (t) lines.push(`${kind}: ${t}${pos}`);
        }
        skipUntil = el;
      } else if (el.getAttribute('aria-current') && !el.closest('[role="tablist"], .cs-tabs')) {
        const t = text(el, 60);
        if (t) lines.push(`Current: ${t}`);
      } else if (tag === 'FIGCAPTION') {
        const t = text(el, 140);
        if (t) {
          // Rendered size of the figure — the model needs to know a page
          // figure is a thumbnail before it narrates "as you can see".
          let sizeNote = '';
          try {
            const img = el.closest('figure')?.querySelector('img');
            const w = img ? Math.round(img.getBoundingClientRect().width) : 0;
            const natural = img?.naturalWidth || 0;
            if (w && natural && natural >= w * 2) sizeNote = ` [thumbnail ${w}px of a ${natural}px image — zoom_into it to read]`;
            else if (w) sizeNote = ` [${w}px wide]`;
          } catch { /* no layout */ }
          lines.push(`Figure: ${t}${sizeNote}${pos}`);
        }
        skipUntil = el;
      } else if (tag === 'BUTTON' || role === 'button' || (tag === 'INPUT' && /^(button|submit|reset)$/i.test(el.type))) {
        const t = text(el, 50) || el.getAttribute('aria-label') || el.value || el.getAttribute('title') || '';
        if (t) {
          const key = `${t.trim()}${el.disabled ? ' (disabled)' : ''}${operatorOnly(el) ? ' (operator-only)' : ''}`;
          if (!seen.has(`btn:${key}`)) { seen.add(`btn:${key}`); buttons.push(`${key}${handles.mint('b', el)}`); }
        }
        skipUntil = el;
      } else if (tag === 'A' && el.getAttribute('href')) {
        const t = text(el, 50) || el.getAttribute('aria-label') || '';
        if (t && !seen.has(`lnk:${t.trim()}`)) { seen.add(`lnk:${t.trim()}`); links.push(`${t.trim()}${handles.mint('l', el)}`); }
        skipUntil = el;
      } else if ((tag === 'INPUT' && !/^(hidden|button|submit|reset|image)$/i.test(el.type)) || tag === 'TEXTAREA' || tag === 'SELECT') {
        const { type, value } = fieldValue(el);
        const label = fieldLabel(el, doc);
        lines.push(`Field "${label || type}"${handles.mint('f', el)} (${type}): ${value}${operatorOnly(el) ? ' (operator-only)' : ''}${pos}`);
      } else if (el.classList.contains('cs-badge') || el.classList.contains('cs-chip') || (role === 'tab' && !el.closest('[role="tablist"], .cs-tabs'))) {
        const t = text(el, 40);
        if (t) push(badges, t);
        skipUntil = el;
      }
      el = walker.nextNode();
    }

    if (describes.length) lines.unshift(...describes.map((d) => `Describe: ${d}`));
    if (notes.length) lines.unshift(...notes.map((n) => `Note: ${n}`));
    // The tab's build vs the deployed one (utils/buildFreshness.js): a stale
    // UI explains a lot of "this looks broken" — NoVA says "reload" first.
    const fresh2 = getBuildFreshness();
    if (fresh2.stale) lines.unshift('App version: OUT OF DATE — a newer build is live; the page should be reloaded (the "Reload" button in the notice at the top, or the browser reload) before anything else is diagnosed.');
    if (badges.length) lines.push(`Badges: ${badges.slice(0, 20).join(' · ')}`);
    if (scrollables.length) lines.push(`Scrollable areas in view: ${scrollables.join(' · ')} (scroll_page target=…)`);
    if (buttons.length) lines.push(`Buttons: ${buttons.slice(0, 40).join(' · ')}`);
    if (links.length) lines.push(`Links: ${links.slice(0, 30).join(' · ')}`);
    const sel = String(win.getSelection?.()?.toString() || '').replace(/\s+/g, ' ').trim();
    if (sel) lines.push(`User highlighted: ${clip(sel, 300)}`);

    let out = lines.filter(Boolean).join('\n');
    if (out.length > maxChars) out = `${out.slice(0, Math.max(0, maxChars - 16))}\n… (truncated)`;
    return out;
  } catch {
    return '';
  }
}

// The system-item text injected into a live realtime session (voice). Keep
// it short — it is re-sent on route changes, at speech start and after the
// agent drives the page.
export function screenContextItem(outline, pageName, pageRoute) {
  if (!outline) return '';
  return `Context: the user's screen is now ${pageName || pageRoute || 'this page'} (${pageRoute || '/'}).\nON SCREEN NOW (captured from their browser; headings, table, button and field labels are exact; "(above)"/"(below)" mark what is scrolled out of view; [b2]/[l1]/[t3]/[f1] are handles you can pass as target to click_element / type_text / set_control / highlight_element; "(operator-only)" controls cannot be pressed by you — point at them; do not read [redacted] values aloud):\n${outline}`;
}

export default captureScreenOutline;
